import React, { useState, useEffect } from 'react';
import type { Customer } from '../../types';

interface CustomerModalProps {
  customer: Customer | null;
  onClose: () => void;
  onSave: (customer: Customer) => void;
  /** Display name of staff verifying student ID. */
  currentStaffLabel?: string;
}

const CustomerModal: React.FC<CustomerModalProps> = ({
  customer,
  onClose,
  onSave,
  currentStaffLabel,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    notes: '',
    studentInstitution: '',
  });
  const [id, setId] = useState<number | null>(null);
  const [loyaltyPoints, setLoyaltyPoints] = useState(0);
  const [studentVerified, setStudentVerified] = useState(false);
  const [studentVerifiedAt, setStudentVerifiedAt] = useState<number | undefined>();
  const [studentVerifiedBy, setStudentVerifiedBy] = useState<string | undefined>();
  const [isResident, setIsResident] = useState(false);
  const [discountPercentage, setDiscountPercentage] = useState(0);

  useEffect(() => {
    if (customer) {
      setFormData({
        name: customer.name,
        phone: customer.phone,
        email: customer.email,
        notes: customer.notes || '',
        studentInstitution: customer.studentInstitution || '',
      });
      setId(customer.id);
      setLoyaltyPoints(customer.loyaltyPoints);
      setStudentVerified(Boolean(customer.studentVerified));
      setStudentVerifiedAt(customer.studentVerifiedAt);
      setStudentVerifiedBy(customer.studentVerifiedBy);
      setIsResident(Boolean(customer.isResident));
      setDiscountPercentage(Number(customer.discountPercentage) || 0);
    } else {
      setFormData({ name: '', phone: '', email: '', notes: '', studentInstitution: '' });
      setId(null);
      setLoyaltyPoints(0);
      setStudentVerified(false);
      setStudentVerifiedAt(undefined);
      setStudentVerifiedBy(undefined);
      setIsResident(false);
      setDiscountPercentage(0);
    }
  }, [customer]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleStudentVerifiedChange = (checked: boolean) => {
    setStudentVerified(checked);
    if (checked) {
      setStudentVerifiedAt(Date.now());
      setStudentVerifiedBy(currentStaffLabel || 'staff');
    } else {
      setStudentVerifiedAt(undefined);
      setStudentVerifiedBy(undefined);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.phone) {
      alert('Please provide at least a name and phone number.');
      return;
    }
    onSave({
      id: id || 0,
      name: formData.name,
      phone: formData.phone,
      email: formData.email,
      notes: formData.notes,
      loyaltyPoints,
      isResident,
      discountPercentage: isResident ? discountPercentage : undefined,
      studentVerified,
      studentVerifiedAt: studentVerified ? studentVerifiedAt : undefined,
      studentVerifiedBy: studentVerified ? studentVerifiedBy : undefined,
      studentInstitution: formData.studentInstitution.trim() || undefined,
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
      <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">
          {customer ? 'Edit Customer' : 'Add New Customer'}
        </h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Full Name
            </label>
            <input
              type="text"
              name="name"
              id="name"
              value={formData.name}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-900 focus:outline-none focus:ring-pink-500 focus:border-pink-500"
              required
            />
          </div>
          <div className="mb-4">
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
              Phone
            </label>
            <input
              type="tel"
              name="phone"
              id="phone"
              value={formData.phone}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-900 focus:outline-none focus:ring-pink-500 focus:border-pink-500"
              required
            />
          </div>
          <div className="mb-4">
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              name="email"
              id="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-pink-500 focus:border-pink-500"
            />
          </div>
          <div className="mb-4">
            <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              name="notes"
              id="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-pink-500 focus:border-pink-500"
            />
          </div>

          <div className="mb-4 p-3 rounded-lg border border-gray-200 bg-gray-50 space-y-3">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-800">
              <input
                type="checkbox"
                checked={studentVerified}
                onChange={(e) => handleStudentVerifiedChange(e.target.checked)}
                className="rounded border-gray-300 text-pink-600 focus:ring-pink-500"
              />
              Student ID verified (in-store)
            </label>
            {studentVerified && (
              <>
                <div>
                  <label
                    htmlFor="studentInstitution"
                    className="block text-xs font-medium text-gray-600 mb-1"
                  >
                    Institution (optional)
                  </label>
                  <input
                    type="text"
                    name="studentInstitution"
                    id="studentInstitution"
                    value={formData.studentInstitution}
                    onChange={handleChange}
                    placeholder="e.g. AUT, University of Auckland"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-gray-900 text-sm"
                  />
                </div>
                {studentVerifiedBy && (
                  <p className="text-xs text-gray-500">
                    Verified by {studentVerifiedBy}
                    {studentVerifiedAt
                      ? ` · ${new Date(studentVerifiedAt).toLocaleString()}`
                      : ''}
                  </p>
                )}
              </>
            )}
          </div>

          <div className="mb-6 p-3 rounded-lg border border-gray-200 bg-gray-50 space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-800">
              <input
                type="checkbox"
                checked={isResident}
                onChange={(e) => setIsResident(e.target.checked)}
                className="rounded border-gray-300 text-pink-600 focus:ring-pink-500"
              />
              Resident / regular discount
            </label>
            {isResident && (
              <div>
                <label
                  htmlFor="discountPercentage"
                  className="block text-xs font-medium text-gray-600 mb-1"
                >
                  Discount %
                </label>
                <input
                  type="number"
                  id="discountPercentage"
                  min={0}
                  max={100}
                  value={discountPercentage}
                  onChange={(e) => setDiscountPercentage(Number(e.target.value) || 0)}
                  className="w-28 px-3 py-2 border border-gray-300 rounded-md bg-white text-sm"
                />
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-pink-500 text-white rounded-md hover:bg-pink-600"
            >
              Save Customer
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CustomerModal;
